import { type CSSProperties, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useTab } from '@/hooks/useTab';
import type { ComponentOrientation } from '@/types';
import Tab, { type TabProps } from './Tab';
import type { BarSizing, TabChild } from './types';
import {
  classes,
  getActiveElementSizing,
  getInitialTab,
  getTabClasses,
  isTabChanged,
} from './utils';

export type { TabChild, TabProps };

export type TabsBlockProps<T extends string[]> = {
  orientation?: ComponentOrientation;
  className?: string;
  style?: CSSProperties;
  bodyClassName?: string;
  bodyStyle?: CSSProperties;
  labelClassName?: string;
  labelStyle?: CSSProperties;
  name: string;
  children: TabChild[];
  defaultTab?: T[number];
  onChange?: (tabName: T[number]) => void;
};

export default function TabsBlock<T extends string[]>({
  orientation = 'horizontal',
  className,
  style = {},
  bodyClassName,
  bodyStyle = {},
  labelClassName,
  labelStyle = {},
  name,
  children,
  defaultTab,
  onChange,
}: TabsBlockProps<T>) {
  const [activeTab, setActiveTab] = useTab(getInitialTab(children, defaultTab));
  const [barSize, setBarSize] = useState<BarSizing>({
    left: '0px',
    width: '0px',
  });

  useEffect(() => {
    const sizing = getActiveElementSizing(name, orientation);
    if (sizing) setBarSize(sizing);
  }, [activeTab, children]);

  useEffect(() => {
    // Handle programmatically passing down a new tab
    if (defaultTab && isTabChanged(children, activeTab, defaultTab)) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);

  const handleSelectTab = (tabName: T[number]) => {
    setActiveTab(tabName);
    onChange?.(tabName);
  };

  return (
    <div
      style={style}
      className={twMerge(
        orientation === 'vertical' && classes.verticalWrapper,
        className
      )}>
      <div
        style={labelStyle}
        className={twMerge(
          classes.labels,
          orientation === 'horizontal' ? classes.horizontal : classes.vertical,
          labelClassName
        )}>
        {children.map((tab, i) => (
          <div
            key={tab.props.name}
            className={getTabClasses(
              tab,
              i,
              children,
              name,
              activeTab,
              orientation
            )}
            onClick={() =>
              !tab.props.disabled && handleSelectTab(tab.props.name)
            }>
            {tab.props.label}
            {tab.props.adornment}
          </div>
        ))}
        <div
          style={barSize}
          className={twMerge(
            classes.bar,
            orientation === 'horizontal'
              ? classes.horizontalBar
              : classes.verticalBar
          )}
        />
      </div>
      <div style={bodyStyle} className={bodyClassName}>
        {children.find(({ props: { name } }) => name === activeTab)}
      </div>
    </div>
  );
}

export { Tab };
